import { memo, useMemo, useState } from 'react';
import { useSubscription } from '../hl/react';
import type { L2Book } from '../hl/types';
import { BOOK_LEVELS, buildBookView, type BookRow } from './book';
import { formatShares, pxToCents, unitsToCents } from './format';
import { useT } from '../i18n/useT';

export interface LevelClick {
  px: string;
  /** Which side of the book was clicked. */
  bookSide: 'bid' | 'ask';
}

interface OrderBookProps {
  /** "#<encoding>" of the selected outcome side (from hl/outcomes.ts). */
  coin: string;
  sideLabel: string;
  maxLevels?: number;
  onLevelClick?: (c: LevelClick) => void;
}

const Row = memo(function Row({
  row,
  kind,
  onClick,
}: {
  row: BookRow;
  kind: 'bid' | 'ask';
  onClick?: (c: LevelClick) => void;
}) {
  const t = useT();
  const cents = pxToCents(row.px);
  const isAsk = kind === 'ask';
  return (
    <li>
      <button
        type="button"
        data-testid={`${kind}-row`}
        aria-label={t(isAsk ? 'book.buy_at' : 'book.bid_at', { px: cents })}
        onClick={() => onClick?.({ px: row.px, bookSide: kind })}
        className="relative grid w-full grid-cols-3 items-center px-3 py-[3px] text-right text-xs hover:bg-surface-2 sm:text-[13px]"
      >
        <span
          aria-hidden
          data-testid="depth-bar"
          className={`absolute inset-y-0 right-0 ${isAsk ? 'bg-no/15' : 'bg-yes/15'}`}
          style={{ width: `${(row.depth * 100).toFixed(2)}%` }}
        />
        <span className={`num relative text-left ${isAsk ? 'text-no' : 'text-yes'}`} title={row.px}>
          {cents}
        </span>
        <span className="num relative">{formatShares(row.sz)}</span>
        <span className="num relative text-muted">{formatShares(row.cum)}</span>
      </button>
    </li>
  );
});

/** An empty row the height of a level, so a thin (or loading / empty) book is as tall as a full one (#2242). */
function PadRow() {
  return (
    <li aria-hidden data-testid="book-pad" className="invisible grid grid-cols-3 px-3 py-[3px] text-xs sm:text-[13px]">
      <span className="num">0</span>
    </li>
  );
}

const pads = (n: number) => Array.from({ length: Math.max(0, n) }, (_, i) => <PadRow key={`pad${i}`} />);

/**
 * Live order book for one outcome side. Subscribes `l2Book` for exactly that coin; HL sends a full
 * snapshot on subscribe and re-sends the full book (~3 s, and on change), so no REST snapshot or diffing
 * is needed and a reconnect re-snapshots by itself. Rows are keyed by price, so updates do not flicker.
 * At most `maxLevels` (BOOK_LEVELS, 6) levels per side, and always that height (#2242): a thin book is padded with empty
 * rows (asks above, bids below), and "loading" / "empty" sit over the empty rows, so nothing under the book moves when
 * the coin, the side or the book changes.
 */
export default function OrderBook({ coin, sideLabel, maxLevels = BOOK_LEVELS, onLevelClick }: OrderBookProps) {
  const t = useT();
  const [latest, setLatest] = useState<L2Book | null>(null);
  useSubscription({ type: 'l2Book', coin }, (data) => {
    if (data.coin === coin) setLatest(data);
  });
  const book = latest?.coin === coin ? latest : null;
  const view = useMemo(() => buildBookView(book, maxLevels), [book, maxLevels]);
  const rows = book !== null && !view.empty;
  const message = book === null ? t('book.loading') : view.empty ? t('book.empty', { side: sideLabel }) : null;

  return (
    // Price/size columns read left to right in every language, Arabic and Hebrew included (docs/I18N.md "Right-to-left").
    <section aria-label={t('book.aria', { side: sideLabel })} className="relative flex flex-col" dir="ltr" data-testid="order-book">
      <div className="grid grid-cols-3 px-3 pb-1 text-right text-[11px] uppercase tracking-wide text-muted">
        <span className="text-left">{t('common.price')}</span>
        <span>{t('common.shares')}</span>
        <span>{t('common.total')}</span>
      </div>
      <ol aria-label={rows ? t('book.asks') : undefined} className="flex flex-col">
        {pads(maxLevels - (rows ? view.asks.length : 0))}
        {rows ? view.asks.map((r) => <Row key={r.px} row={r} kind="ask" onClick={onLevelClick} />) : null}
      </ol>
      <div
        data-testid={rows ? 'book-mid' : undefined}
        aria-hidden={rows ? undefined : true}
        className={`my-1 flex items-center justify-between border-y px-3 py-1.5 text-xs ${
          rows ? 'border-border bg-surface-2/60' : 'invisible border-transparent'
        }`}
      >
        <span>
          <span className="text-muted">{t('common.mid')} </span>
          <span className="num font-medium" data-testid={rows ? 'mid-value' : undefined}>
            {view.mid === null ? '—' : unitsToCents(view.mid)}
          </span>
        </span>
        <span>
          <span className="text-muted">{t('book.spread')} </span>
          <span className="num" data-testid={rows ? 'spread-value' : undefined}>
            {view.spread === null ? '—' : unitsToCents(view.spread)}
          </span>
        </span>
      </div>
      <ol aria-label={rows ? t('book.bids') : undefined} className="flex flex-col">
        {rows ? view.bids.map((r) => <Row key={r.px} row={r} kind="bid" onClick={onLevelClick} />) : null}
        {pads(maxLevels - (rows ? view.bids.length : 0))}
      </ol>
      {message !== null ? (
        <p
          className="absolute inset-x-0 bottom-0 top-6 flex items-center justify-center px-3 text-center text-sm text-muted"
          data-testid={book === null ? undefined : 'book-empty'}
        >
          {message}
        </p>
      ) : null}
    </section>
  );
}
